import { AuthLoginByOtacHelper } from '@sravni/utils/lib/auth';

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { intervals, timeouts } from '../../constants/authorization';
import { IAuthSMSRequest, IAuthSMSVerifyRequest } from '../../interfaces/auth';
import { TPhoneConfirmationStatuses } from '../../interfaces/phoneVerifier';
import { signinUser, tryAuth, verifyUser } from '../../services/user';
import { GlobalState } from '../index';
import { getSmsCodeStatusTimeout } from '../selectors/authorization';

const name = 'authorization';

export interface IAuthorizationState {
  phoneConfirmation: {
    status: TPhoneConfirmationStatuses;
    isLoading: boolean;
  };
  smsCodeStatus: {
    timeout: number;
    errorMessage: string;
  };
}

const initialState: IAuthorizationState = {
  phoneConfirmation: {
    status: 'required',
    isLoading: false,
  },
  smsCodeStatus: {
    timeout: 0,
    errorMessage: null,
  },
};

export const trySendSms = createAsyncThunk<string, IAuthSMSRequest, { state: GlobalState }>(
  `${name}/trySendSms`,
  async (values: IAuthSMSRequest, { dispatch, getState }) => {
    await signinUser({ name: values.name, phone: values.phone, email: values.email });

    dispatch(setSmsCodeStatusTimeout(timeouts.initialSmsTimeoutInSeconds));

    const intervalId = setInterval(() => {
      const currentTimeout = getSmsCodeStatusTimeout(getState());

      if (currentTimeout <= 1) {
        clearInterval(intervalId);
      }

      dispatch(setSmsCodeStatusTimeout(currentTimeout - 1));
    }, intervals.smsCountdownInMilliseconds);

    return values.phone;
  },
);

export const trySubmitSms = createAsyncThunk<string, { values: IAuthSMSVerifyRequest; onSuccessAuth?: () => void }>(
  `${name}/trySubmitSms`,
  async ({ values, onSuccessAuth }) => {
    const authHelper = new AuthLoginByOtacHelper({
      type: 'modal',
    });
    // Эта функция должна вызываться как можно раньше после последнего действия пользователя
    // Потому что например для сафари - если пройдет более 1 секунды, то сафари заблокирует открытие
    authHelper.prepare();

    const response = await verifyUser({
      code: values.code,
      phone: values.phone,
    });

    if (!response || response.error) {
      authHelper.destroy();
      throw new Error(response.error.message ?? 'Empty response');
    }

    await tryAuth(authHelper, { otac: response.otac, username: response.username }, onSuccessAuth);

    return values.phone;
  },
);

const authorizationSlice = createSlice({
  name,
  initialState,
  reducers: {
    savePhoneConfirmationInfo: (state, { payload }: PayloadAction<IAuthorizationState['phoneConfirmation']>) => {
      state.phoneConfirmation.status = payload.status;
      state.phoneConfirmation.isLoading = payload.isLoading;
    },
    setPhoneConfirmationStatus: (state, { payload }: PayloadAction<TPhoneConfirmationStatuses>) => {
      state.phoneConfirmation.status = payload;
    },
    setSmsCodeStatusTimeout: (state, { payload }: PayloadAction<number>) => {
      state.smsCodeStatus.timeout = payload;
    },
    setSmsCodeStatusError: (state, { payload }: PayloadAction<string>) => {
      state.smsCodeStatus.errorMessage = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(trySendSms.pending, (state) => {
      state.phoneConfirmation.isLoading = true;
    });
    builder.addCase(trySendSms.fulfilled, (state) => {
      state.phoneConfirmation.status = 'waiting';
      state.phoneConfirmation.isLoading = false;
    });
    builder.addCase(trySendSms.rejected, (state) => {
      state.phoneConfirmation.status = 'required';
      state.phoneConfirmation.isLoading = false;
    });
    builder.addCase(trySubmitSms.pending, (state) => {
      state.phoneConfirmation.isLoading = true;
    });
    builder.addCase(trySubmitSms.fulfilled, (state) => {
      state.phoneConfirmation.status = 'completed';
      state.phoneConfirmation.isLoading = false;

      state.smsCodeStatus.errorMessage = null;
    });
    builder.addCase(trySubmitSms.rejected, (state, { error }) => {
      state.smsCodeStatus.errorMessage = error.message;
      state.phoneConfirmation.isLoading = false;
    });
  },
});

const { reducer, actions } = authorizationSlice;

export const { setPhoneConfirmationStatus, setSmsCodeStatusTimeout, setSmsCodeStatusError } = actions;

export default reducer;
